import * as React from 'react';
import { useMemo, useState, useEffect } from 'react';
import {
  Card, Typography, Stack, Box, FormControl, RadioGroup, Radio,
  FormControlLabel, TextField, Autocomplete,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList, PieChart, Pie, Legend,
} from 'recharts';
import { getLightPropostas, getUsuarios } from './api';
import { cores, coresGrafico } from '../styles/styleUtils';

const CORES = coresGrafico;

// Etapas do fluxo de Vendas (fluxo 2)
const ETAPAS_VENDAS = [
  'Orçamento Realizado', 'Agendar Entrega', 'Orçamento Entregue',
  'Negociação', 'Fechamento', 'Assinar Contrato', 'Coleta de Dados',
];
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// Valor comercial de uma linha de serviço (recorrente multiplica pela duração)
const valorLinha = (s) =>
  (Number(s.valor) || 0) * (Number(s.quantidade) || 0) *
  (s.tipo === 'recorrente' ? (Number(s.duracao) || 1) : 1);

function KpiCard({ titulo, valor, cor = '#5B8FF9', sub }) {
  return (
    <Card sx={{
      p: 2, textAlign: 'center', backgroundColor: '#F5F7FA',
      border: `1px solid ${cor}`, borderRadius: 2, minWidth: 150, flex: 1,
    }}>
      <Typography variant="caption" color="text.secondary">{titulo}</Typography>
      <Typography variant="h5" fontWeight="bold" color={cor}>{valor}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Card>
  );
}

export default function HomeVendasServicos({ nomesServico }) {
  const hoje = new Date();
  const inicioAno = `${hoje.getFullYear()}-01-01`;
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];

  const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
  const isSupervisor = usuarioLogado?.supervisor;
  const [usuarios, setUsuarios] = useState([]);

  useEffect(() => {
    if (!isSupervisor) return;
    getUsuarios().then((u) => setUsuarios(u || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filtro, setFiltro] = useState({ dataInicial: inicioAno, dataFinal: ultimoDia, vendedor: null });
  const [radioTop, setRadioTop] = useState('quantidade');
  const [propostas, setPropostas] = useState([]);

  // Busca própria por intervalo. Serviços são contados independentemente do fluxo
  // (mesmo critério "quadro completo" da Home de Vendas). Receita = valor de venda.
  useEffect(() => {
    const fetchServicos = async () => {
      const result = await getLightPropostas({
        datainicial: filtro.dataInicial,
        datafinal: filtro.dataFinal,
        usuarioLogado,
      });
      setPropostas(result || []);
    };
    fetchServicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro.dataInicial, filtro.dataFinal]);

  const nomesSet = useMemo(() => new Set(nomesServico || []), [nomesServico]);

  // Apenas propostas de serviço + filtro local por vendedor
  const propostasServ = useMemo(() => (propostas || []).filter((p) => {
    if (!nomesSet.has(p.produto?.nome)) return false;
    if (filtro.vendedor && p.user?._id !== filtro.vendedor._id && p.user2?._id !== filtro.vendedor._id) return false;
    return true;
  }), [propostas, nomesSet, filtro.vendedor]);

  // CORRIGIDO (deployment Marcki — Home contando propostas ainda não
  // fechadas como "vendidas"): propostasServ inclui TODAS as propostas de
  // Serviço, em qualquer etapa (necessário para o Funil, que precisa
  // mostrar o pipeline completo). Os KPIs e gráficos de "vendido" abaixo
  // devem contar só o que realmente fechou — mesmo critério já usado no
  // Funil para "Venda Aprovada": fluxo.id === 100000000 (proposta avançou
  // de Vendas para Engenharia de Serviços).
  const propostasServVendidas = useMemo(
    () => propostasServ.filter((p) => p.fluxo?.id === 100000000),
    [propostasServ],
  );

  // KPIs — receita = valor de venda (só propostas efetivamente vendidas)
  const totalServicos = propostasServVendidas.length;
  const totalReceita = propostasServVendidas.reduce((s, p) => s + (Number(p.valor) || 0), 0);
  const ticketMedio = totalServicos ? totalReceita / totalServicos : 0;

  // Funil de serviços pelas etapas de Vendas (fluxo 2) + "Venda Aprovada"
  const dadosFunil = useMemo(() => {
    const etapas = ETAPAS_VENDAS.map((nome, i) => ({
      name: nome,
      quantidade: propostasServ.filter((p) => p.fluxo?.id === 2 && p.etapa?.nome === nome).length,
      fill: CORES[i % CORES.length],
    }));
    // Serviços que já avançaram além de Vendas (venda fechada) — fluxo de Engenharia de Serviços
    etapas.push({
      name: 'Venda Aprovada',
      quantidade: propostasServ.filter((p) => p.fluxo?.id === 100000000).length,
      fill: '#5AD8A6',
    });
    return etapas;
  }, [propostasServ]);

  // Agregação por tipo de serviço: quantidade + receita (valor de venda rateado pela linha)
  // — só propostas efetivamente vendidas (ver propostasServVendidas acima).
  const porTipo = useMemo(() => {
    const mapa = {};
    propostasServVendidas.forEach((p) => {
      const linhas = p.servicos || [];
      const totalLinhas = linhas.reduce((s, x) => s + valorLinha(x), 0);
      const valorVenda = Number(p.valor) || 0;
      linhas.forEach((s) => {
        const nome = s.nome || 'Outros';
        if (!mapa[nome]) mapa[nome] = { name: nome, quantidade: 0, receita: 0 };
        mapa[nome].quantidade += 1;
        const peso = totalLinhas > 0
          ? valorLinha(s) / totalLinhas
          : (linhas.length ? 1 / linhas.length : 0);
        mapa[nome].receita += valorVenda * peso;
      });
    });
    return Object.values(mapa).map((v, i) => ({
      ...v,
      receita: parseFloat(v.receita.toFixed(2)),
      fill: CORES[i % CORES.length],
    }));
  }, [propostasServVendidas]);

  const porTipoQtd = useMemo(() => [...porTipo].sort((a, b) => b.quantidade - a.quantidade), [porTipo]);
  const topServicos = useMemo(
    () => [...porTipo]
      .sort((a, b) => (radioTop === 'receita' ? b.receita - a.receita : b.quantidade - a.quantidade))
      .slice(0, 10),
    [porTipo, radioTop],
  );

  // Receita por semana do mês corrente — só propostas efetivamente vendidas
  const receitaSemanas = useMemo(() => {
    const semanas = { 'Semana 1': 0, 'Semana 2': 0, 'Semana 3': 0, 'Semana 4': 0, 'Semana 5': 0 };
    propostasServVendidas.forEach((p) => {
      const d = new Date(p.data);
      if (d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()) {
        const semana = Math.min(5, Math.ceil(d.getDate() / 7));
        semanas[`Semana ${semana}`] += Number(p.valor) || 0;
      }
    });
    return Object.entries(semanas).map(([name, receita]) => ({ name, receita: parseFloat(receita.toFixed(2)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propostasServVendidas]);

  return (
    <Box>
      {/* Filtro */}
      <Stack direction="row" gap={2} flexWrap="wrap" mb={3} alignItems="center">
        <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
          value={filtro.dataInicial} onChange={(e) => setFiltro((f) => ({ ...f, dataInicial: e.target.value }))} />
        <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
          value={filtro.dataFinal} onChange={(e) => setFiltro((f) => ({ ...f, dataFinal: e.target.value }))} />
        {isSupervisor && (
          <Autocomplete size="small" sx={{ minWidth: 220 }} options={usuarios || []}
            getOptionLabel={(o) => o.nome} value={filtro.vendedor || null}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            onChange={(_, v) => setFiltro((f) => ({ ...f, vendedor: v }))}
            renderInput={(p) => <TextField {...p} label="Vendedor" />} />
        )}
      </Stack>

      {/* KPIs */}
      <Stack direction="row" gap={2} flexWrap="wrap" mb={4}>
        <KpiCard titulo="Serviços Vendidos" valor={fmtNum(totalServicos)} />
        <KpiCard titulo="Receita de Venda" valor={fmtBRL(totalReceita)} cor="#3AA76D" />
        <KpiCard titulo="Ticket Médio" valor={fmtBRL(ticketMedio)} cor="#8B5CC7" />
      </Stack>

      {/* Funil de serviços (Vendas) */}
      <Card sx={{ p: 3, mb: 4, backgroundColor: '#FAFAFA' }}>
        <Typography variant="h6" fontWeight="bold" color={cores.laranja} mb={2}>Funil de Serviços por Etapa (Vendas)</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dadosFunil} layout="vertical" margin={{ left: 60, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${v} serviço(s)`, 'Quantidade']} />
            <Bar dataKey="quantidade" radius={[0, 4, 4, 0]}>
              {dadosFunil.map((e, i) => <Cell key={i} fill={e.fill} />)}
              <LabelList dataKey="quantidade" position="right" style={{ fontSize: 12, fontWeight: 'bold' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Barras (tipos por quantidade) + Pizza */}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mb={4}>
        <Card sx={{ p: 3, flex: 1, backgroundColor: '#FAFAFA' }}>
          <Typography variant="h6" fontWeight="bold" color={cores.laranja} mb={2}>Tipos de Serviço por Quantidade</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={porTipoQtd} margin={{ left: 0, right: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={70} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(v) => [`${v} serviço(s)`, 'Quantidade']} />
              <Bar dataKey="quantidade" fill="#5AD8A6" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="quantidade" position="top" style={{ fontSize: 11, fontWeight: 'bold' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card sx={{ p: 3, flex: 1, backgroundColor: '#FAFAFA' }}>
          <Typography variant="h6" fontWeight="bold" color={cores.laranja} mb={2}>Distribuição de Tipos de Serviço</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={porTipoQtd} dataKey="quantidade" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                {porTipoQtd.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} serviço(s)`, n]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Stack>

      {/* Top serviços (quantidade × receita) */}
      <Card sx={{ p: 3, mb: 4, backgroundColor: '#FAFAFA' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight="bold" color={cores.laranja}>Top Serviços Vendidos</Typography>
          <FormControl>
            <RadioGroup row value={radioTop} onChange={(e) => setRadioTop(e.target.value)}>
              <FormControlLabel value="quantidade" control={<Radio size="small" />} label="Quantidade" />
              <FormControlLabel value="receita" control={<Radio size="small" />} label="Receita" />
            </RadioGroup>
          </FormControl>
        </Stack>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={topServicos} layout="vertical" margin={{ left: 60, right: 70 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(v) => (radioTop === 'receita' ? fmtBRL(v) : v)} />
            <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => (radioTop === 'receita' ? [fmtBRL(v), 'Receita'] : [`${v} serviço(s)`, 'Quantidade'])} />
            <Bar dataKey={radioTop} fill="#945FB9" radius={[0, 4, 4, 0]}>
              <LabelList dataKey={radioTop} position="right" style={{ fontSize: 11, fontWeight: 'bold' }}
                formatter={(v) => (radioTop === 'receita' ? fmtBRL(v) : v)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Receita por semana (mês corrente) */}
      <Card sx={{ p: 3, mb: 4, backgroundColor: '#FAFAFA' }}>
        <Typography variant="h6" fontWeight="bold" color={cores.laranja} mb={2}>Receita por Semana (mês corrente)</Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={receitaSemanas} margin={{ left: 10, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => fmtBRL(v)} width={90} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [fmtBRL(v), 'Receita']} />
            <Bar dataKey="receita" radius={[4, 4, 0, 0]}>
              {receitaSemanas.map((e, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </Box>
  );
}